// @ts-check
import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { cartRequest } from '@theme/custom.cart-request';

/** Group keystrokes into one request. Checkout explicitly waits for the save. */
const SAVE_DEBOUNCE_MS = 400;

/**
 * @typedef {object} CartItemNoteRefs
 * @property {HTMLTextAreaElement} [note] - The note textarea.
 * @property {HTMLElement} [counter] - The "n/240" character counter.
 * @property {HTMLElement} [error] - The "note not saved" line under the field.
 */

/**
 * The cart's single Order Comments field, saved as the order note. Shown only when the
 * cart holds a line that skipped the customizer; snippets/custom.cart-item-note.liquid owns that
 * rule and explains the historic file name. The cart is the source of truth; this holds no state
 * of its own beyond the last value it successfully saved.
 *
 * @extends {Component<CartItemNoteRefs>}
 */
class CustomCartItemNote extends Component {
  /** Serializes saves: concurrent updates to one cart race server-side. @type {Promise<unknown>} */
  #queue = Promise.resolve();
  #pendingSaves = 0;

  /** Last value typed here, held until a save confirms it. @type {string | null} */
  #draft = null;

  /**
   * The note a clear was already sent for. A failed clear puts it back on `data-note`, which the
   * observer sees, so without this the retry loops against /cart/update.js. @type {string | null}
   */
  #clearAttempted = null;

  /**
   * KEY-DECISION 2026-09-22: MutationObserver, not connectedCallback or updatedCallback. A morph
   * reuses this element, and morph.js hands `onAfterUpdate` the DOMParser node, which is never
   * upgraded - same trap and answer as assets/custom.cart-alerts.js. Attributes only, no subtree,
   * so marking the textarea below cannot re-enter.
   */
  #observer = new MutationObserver(() => this.#afterRender());

  connectedCallback() {
    super.connectedCallback();
    this.#afterRender();
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-clear', 'data-note', 'note-pending'],
    });
  }

  disconnectedCallback() {
    this.#saveDraft.cancel();
    this.#observer.disconnect();
    super.disconnectedCallback();
    this.#syncWallets();
  }

  /** First paint, and every cart morph that reaches this element. */
  #afterRender() {
    if (this.hasAttribute('data-clear')) {
      this.#clear();
      return;
    }
    this.#markDraft();
    this.#syncWallets();
  }

  /**
   * Flags the field while it holds unconfirmed text, so morph.js's `onBeforeUpdate` carries it
   * across a render. Not repairable after the fact: that morph mutates nothing observable.
   */
  #markDraft() {
    this.refs.note?.toggleAttribute('data-preserve-draft', this.#draft != null);
  }

  get pending() {
    // The clear stub has no field and no error surface, so a clear that keeps failing would
    // swallow every Checkout click with nothing on screen to explain it. Nothing waits on it.
    if (this.hasAttribute('data-clear')) return false;
    return this.#pendingSaves > 0 || (this.refs.note?.value ?? '') !== (this.dataset.note ?? '');
  }

  async flush() {
    this.#saveDraft.cancel();
    await this.#save();
    return !this.pending;
  }

  #syncWallets() {
    this.toggleAttribute('note-pending', this.pending);
    const blocked = !!document.querySelector(
      'custom-cart-item-note[note-pending], cart-discount-component[wallet-guarded]'
    );
    for (const wallets of document.querySelectorAll('.additional-checkout-buttons')) {
      wallets.toggleAttribute('inert', blocked);
    }
  }

  /**
   * Updates the live counter and debounces the save.
   * @param {Event} event
   */
  onInput(event) {
    if (!(event.target instanceof HTMLTextAreaElement)) return;

    this.#draft = event.target.value;
    this.#renderCount();
    this.#markDraft();
    this.#syncWallets();
    void this.#saveDraft();
  }

  #renderCount() {
    const { counter, note } = this.refs;
    if (counter && note) counter.textContent = `${note.value.length}/${this.#maxChars}`;
  }

  /**
   * Drops a note the shopper can no longer reach. No error surface and nothing waits on it: this
   * element has no UI, and the next cart page render asks again if the write did not land.
   */
  #clear() {
    const stale = this.dataset.note ?? '';
    if (!stale || stale === this.#clearAttempted) return;
    this.#clearAttempted = stale;
    this.dataset.note = '';
    void this.#update('').catch((error) => {
      console.error(error);
      this.dataset.note = stale;
    });
  }

  /**
   * Leaving the field saves what is in it now. Without this a shopper who types and goes straight
   * to Checkout loses up to one debounce window, and the click that navigates blurs first.
   */
  onBlur() {
    this.#saveDraft.cancel();
    void this.#save();
  }

  #saveDraft = debounce(() => this.#save(), SAVE_DEBOUNCE_MS);

  /**
   * Silent save (the gift-message draft pattern in assets/custom.cart-upsells.js): no section
   * bundle and no CartUpdateEvent, because a morph mid-typing would replace the textarea under the
   * caret. Only a successful response advances the saved value, so a failure remains retryable.
   */
  #save() {
    const value = this.refs.note?.value ?? '';
    this.#pendingSaves += 1;
    this.#syncWallets();
    this.#queue = this.#queue
      .then(async () => {
        if (value === (this.dataset.note ?? '')) {
          if (this.#draft === value) this.#draft = null;
          return;
        }
        await this.#update(value);
        this.dataset.note = value;
        if (this.#draft === value) this.#draft = null;
        if (this.refs.error) this.refs.error.hidden = true;
      })
      .catch((error) => {
        console.error(error);
        if (this.refs.error) this.refs.error.hidden = false;
      })
      .finally(() => {
        this.#pendingSaves -= 1;
        this.#markDraft();
        this.#syncWallets();
      });
    return this.#queue;
  }

  /**
   * Writes the note onto the cart. `note` is a cart-level field, so unlike a line-item property
   * this replaces nothing else the cart carries and needs no read-back.
   * @param {string} value - The note to store; an empty one clears it.
   */
  async #update(value) {
    await cartRequest(Theme.routes.cart_update_url, {
      ...fetchConfig('json', { body: JSON.stringify({ note: value }) }),
      // A shopper who types and then clicks a header link blurs the field on the way out, which
      // starts this save; without keepalive the unload cancels it and the note is silently lost.
      keepalive: true,
    });
  }

  get #maxChars() {
    return Number(this.dataset.maxChars) || 240;
  }
}

if (!customElements.get('custom-cart-item-note')) {
  customElements.define('custom-cart-item-note', CustomCartItemNote);

  /** @returns {CustomCartItemNote[]} */
  const pendingNotes = () =>
    [...document.querySelectorAll('custom-cart-item-note')]
      .filter((note) => note instanceof CustomCartItemNote && note.pending)
      .map((note) => /** @type {CustomCartItemNote} */ (note));

  let resuming = false;
  let waiting = false;
  /** @param {Event} event */
  const beforeCartAction = async (event) => {
    if (resuming || !(event.target instanceof Element)) return;
    const target = event.target.closest('button, a, input[type="checkbox"], input[type="radio"]');
    const form =
      event.target instanceof HTMLFormElement && event.target.id === 'cart-form'
        ? event.target
        : null;
    if (
      !form &&
      !(
        target instanceof HTMLElement &&
        (target.closest('.cart-page') || target.matches('[name="checkout"]'))
      )
    )
      return;
    const notes = pendingNotes();
    if (!notes.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (waiting) return;
    waiting = true;
    try {
      if (!(await Promise.all(notes.map((note) => note.flush()))).every(Boolean)) return;
      if (pendingNotes().length) return;
      resuming = true;
      if (form && event instanceof SubmitEvent) {
        form.requestSubmit(event.submitter);
      } else if (target instanceof HTMLElement && target.isConnected) {
        target.click();
      }
    } finally {
      resuming = false;
      waiting = false;
    }
  };
  // On window, not document: component.js captures every `on:click` on document first, so a hold
  // there would let the quantity buttons act twice. Window capture runs ahead of all of them.
  window.addEventListener(
    'click',
    (event) => {
      void beforeCartAction(event);
    },
    true
  );
  window.addEventListener(
    'submit',
    (event) => {
      void beforeCartAction(event);
    },
    true
  );
}
